import {
  Form,
  FormMultiLineTextInput,
  FormTextInput,
  SingleSelectDropdown,
  UploadImagePreview,
  UploadInput,
} from 'ck-components';
import {
  CreateNewsPostV1,
  NewsPost,
  useEventsIdName,
  useNewsCreate,
  useNewsEdit,
} from 'ck-queries';
import { SubmitErrorHandler, SubmitHandler, useForm } from 'react-hook-form';
import { convertFileToUrlString, useTypedNavigate } from 'ck-utils';
import { useEffect, useRef, useState } from 'react';

import { useOrganizerCommittees } from '../../queries/organizer/useCommittees';
import toast from 'react-hot-toast';

const titleMaxLength = 200;
const bodyMaxLength = 5000;

interface Props {
  isEdit?: boolean;
  newsId?: string;
  prefill?: NewsPost;
}

export function CreateNewsForm({ isEdit, newsId, prefill }: Props) {
  const Q_committees = useOrganizerCommittees();
  const Q_events = useEventsIdName();
  const M_createNews = useNewsCreate();
  const M_editNews = useNewsEdit(newsId ?? '');
  const { navigate } = useTypedNavigate();

  const form = useForm<CreateNewsPostV1>();
  const [imagePreviewUrl, setImagePreviewUrl] = useState<string | undefined>();

  const image = form.watch('image');

  // Only prefill once, so a background refetch doesn't clobber unsaved edits
  const hasPrefilled = useRef(false);

  useEffect(() => {
    if (prefill && !hasPrefilled.current) {
      hasPrefilled.current = true;
      form.reset({
        committeeId: prefill.committeeId,
        title: prefill.title,
        titleSwedish: prefill.titleSwedish ?? '',
        body: prefill.body,
        bodySwedish: prefill.bodySwedish ?? '',
        eventId: prefill.eventId ?? '',
      });
      setImagePreviewUrl(prefill.imageUrl);
    }
  }, [prefill]);

  useEffect(() => {
    if (image instanceof File) {
      convertFileToUrlString(image).then(
        (url) => url && setImagePreviewUrl(url)
      );
    }
  }, [image]);

  const onInvalid: SubmitErrorHandler<CreateNewsPostV1> = () => {
    toast.error('Fyll i alla obligatoriska fält');
  };

  const onSubmit: SubmitHandler<CreateNewsPostV1> = (data) => {
    if (isEdit) {
      M_editNews.mutate(data, {
        onSuccess: () => {
          navigate('/news/archive');
        },
      });
      return;
    }
    M_createNews.mutate(data, {
      onSuccess: () => {
        form.reset();
        setImagePreviewUrl(undefined);
      },
    });
  };

  return (
    <Form
      buttonType='button'
      submitButtonStyle='w-52'
      form={form}
      onSubmit={() => {
        form.handleSubmit(onSubmit, onInvalid)();
      }}
      submitButtonLabel={isEdit ? 'Spara ändringar' : 'Publicera'}
    >
      {Q_committees.data && (
        <SingleSelectDropdown
          withSearch
          label='Avsändare'
          name='committeeId'
          form={form}
          values={Q_committees.data.map((committee) => committee.id)}
          labelFromValue={(id) =>
            Q_committees.data?.find((committee) => committee.id === id)?.name ??
            ''
          }
        />
      )}
      <UploadInput
        parentForm={form}
        label='Bild'
        name='image'
        accept='.jpeg, .jpg, .png, .svg'
        emptyState='Ladda upp .png, .jpg, .svg'
        description={''}
      />
      {imagePreviewUrl && (
        <div className='mb-5'>
          <UploadImagePreview imageUrl={imagePreviewUrl} />
        </div>
      )}
      <FormTextInput
        label='Titel (ENGELSKA)'
        name='title'
        parentForm={form}
        maxLength={titleMaxLength}
        description=''
      />
      <FormTextInput
        label='Titel (SVENSKA)'
        name='titleSwedish'
        parentForm={form}
        optional
        maxLength={titleMaxLength}
        description=''
      />
      <FormMultiLineTextInput
        label='Innehåll (ENGELSKA)'
        name='body'
        parentForm={form}
        maxLength={bodyMaxLength}
      />
      <FormMultiLineTextInput
        label='Innehåll (SVENSKA)'
        name='bodySwedish'
        parentForm={form}
        optional
        maxLength={bodyMaxLength}
      />
      {Q_events.data && (
        <SingleSelectDropdown
          withSearch
          label='Länka event'
          name='eventId'
          form={form}
          emptyStateLabel='Ingen länk till event'
          values={Q_events.data.map(({ id }) => id)}
          labelFromValue={(id) =>
            Q_events.data?.find((event) => event.id === id)?.name ?? ''
          }
        />
      )}
    </Form>
  );
}
